import React, { useState } from "react";

/*
  Timer chip shown in the chat header for Club<->Agent conversations. Both
  sides see the remaining time; only the club side receives the extend control
  (driven by the `canExtend` prop — the parent mirrors the backend gate).

  Presentational only: all state comes in via props so it renders identically
  in the app, in Jest and in the dev harness. Time is passed pre-formatted as
  `{ count, phrase, urgent, expired }` with the number kept separate from its
  label so the static translator never has to reassemble a digit with a word.

  Styling follows the existing header badges (small rounded pill) and the
  existing header menu popover — no new design language.
*/

const ClockIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" strokeWidth={1.6} />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.6}
      d="M12 7.5V12l3 2"
    />
  </svg>
);

const ChatTimerChip = ({
  remaining,
  canExtend = false,
  extensionChoices = [5, 10, 14],
  onExtend,
  isExtending = false,
  // Deactivation (design 1m): the rights holder can make THIS thread permanent.
  // Shown one level deeper than Extend — inside the same popover, below the
  // extend choices — as deliberate friction for a permanent decision. The chip
  // only raises the intent (opens the modal); the header owns the modal + call.
  canDeactivate = false,
  onDeactivate,
  // Dev/preview only: force the extend popover open (used by the screenshot
  // harness). No effect in the app, where the trigger drives it.
  defaultExtendOpen = false,
  // "header" renders the thread-header pill from design 3a/1m/7b: amber
  // "Expires in N days" (green ∞ Permanent, gray Expired), NO embedded
  // controls — the header owns the standalone Extend button and the kebab
  // TIMER section. Default ("inline") keeps the original pill+dropdown so its
  // unit tests and any other caller stay unaffected.
  variant = "inline",
}) => {
  const [showExtend, setShowExtend] = useState(defaultExtendOpen);

  if (!remaining) return null;

  const { count, phrase, urgent, expired, permanent } = remaining;

  // Thread-header pill (design 3a §"Expires in N days"). Number kept in its own
  // data-no-translate node so the static translator never reassembles a digit
  // with a word (same contract as the inline variant).
  if (variant === "header") {
    const headerTone = permanent
      ? "bg-[#EAF6EE] text-[#17803D]"
      : expired
      ? "bg-[#F2F4F7] text-[#98A2B3]"
      : "bg-[#FFF4E5] text-[#B54708]";
    const unit =
      phrase === "day left"
        ? "day"
        : phrase === "hour left"
        ? "hour"
        : phrase === "hours left"
        ? "hours"
        : "days";
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full text-[12px] font-semibold ${headerTone}`}
        style={{ padding: "4px 11px" }}
        data-testid="chat-timer-chip"
      >
        {permanent ? (
          <span data-no-translate="true">{phrase}</span>
        ) : expired || count == null ? (
          <>
            {!expired && <ClockIcon className="w-[11px] h-[11px] flex-shrink-0" />}
            <span>{phrase}</span>
          </>
        ) : (
          <>
            <ClockIcon className="w-[11px] h-[11px] flex-shrink-0" />
            <span>Expires in</span>
            <span className="tabular-nums" data-no-translate="true">
              {count}
            </span>
            <span>{unit}</span>
          </>
        )}
      </span>
    );
  }

  // Calm by default; amber under a day; muted once expired; green once permanent.
  const tone = permanent
    ? "bg-[#EAF6EE] text-[#17803D]"
    : expired
    ? "bg-gray-100 text-gray-500"
    : urgent
    ? "bg-[#FEF0C7] text-[#B54708]"
    : "bg-[#F2F4F7] text-[#475467]";

  // A permanent thread has no countdown and no extend/deactivate controls.
  const showControls = !permanent && (canExtend || canDeactivate);

  const handleExtend = async (days) => {
    if (isExtending) return;
    if (onExtend) {
      await onExtend(days);
    }
    setShowExtend(false);
  };

  const handleDeactivate = () => {
    setShowExtend(false);
    if (onDeactivate) onDeactivate();
  };

  return (
    <span
      className="relative inline-flex items-center"
      data-testid="chat-timer-chip"
    >
      <span
        className={`inline-flex items-center gap-1.5 pl-2 pr-2 py-1 rounded-full text-[11px] font-medium ${tone}`}
      >
        {permanent ? (
          <span className="font-semibold">{phrase}</span>
        ) : (
          <>
            <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" />
            {count !== null && count !== undefined && (
              <span
                className="font-semibold tabular-nums"
                data-no-translate="true"
              >
                {count}
              </span>
            )}
            <span>{phrase}</span>
          </>
        )}

        {showControls && (
          <button
            type="button"
            onClick={() => setShowExtend((prev) => !prev)}
            className="ml-0.5 -mr-1 w-5 h-5 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors"
            aria-label={canExtend ? "Extend conversation" : "Timer options"}
            aria-haspopup="true"
            aria-expanded={showExtend}
            data-testid="chat-timer-extend-trigger"
            disabled={isExtending}
          >
            {isExtending ? (
              <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            )}
          </button>
        )}
      </span>

      {showControls && showExtend && (
        <>
          {/* Backdrop closes the popover on outside click (header pattern). */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowExtend(false)}
          />
          <div
            className={`absolute left-0 top-full mt-2 ${
              canDeactivate ? "w-60" : "w-44"
            } bg-white rounded-lg shadow-lg border border-[#E5E7EB] z-50 overflow-hidden`}
            data-testid="chat-timer-extend-popover"
            role="menu"
          >
            {canExtend && (
              <>
                <div className="px-3 py-2 text-[11px] text-[#6C737F] border-b border-[#F0F1F3]">
                  Extend conversation
                </div>
                <div className="py-1">
                  {extensionChoices.map((days) => (
                    <button
                      key={days}
                      type="button"
                      role="menuitem"
                      onClick={() => handleExtend(days)}
                      disabled={isExtending}
                      className="w-full px-3 py-2 text-left text-sm text-[#1E1E1E] hover:bg-gray-50 flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`chat-timer-extend-option-${days}`}
                    >
                      <span
                        className="font-semibold tabular-nums"
                        data-no-translate="true"
                      >
                        +{days}
                      </span>
                      <span>days</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {canDeactivate && (
              <>
                {canExtend && <div className="border-t border-[#F0F1F3]" />}
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleDeactivate}
                  disabled={isExtending}
                  className="w-full px-3 py-2.5 text-left hover:bg-gray-50 flex items-start gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="chat-timer-deactivate-option"
                >
                  <span
                    className="text-[#1466D8] font-bold leading-none mt-0.5"
                    data-no-translate="true"
                    aria-hidden="true"
                  >
                    ∞
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm text-[#1E1E1E]">
                      Deactivate timer for this thread
                    </span>
                    <span className="text-[11px] text-[#6C737F]">
                      This thread never expires
                    </span>
                  </span>
                </button>
              </>
            )}
          </div>
        </>
      )}
    </span>
  );
};

export default ChatTimerChip;
